import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { Telegram } from "@mui/icons-material";

export default function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-300 pt-16 pb-8 mt-20 font-sans border-t-4 border-green-600 shadow-2xl relative overflow-hidden">
      {/* Decorative gradient overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-gray-900 via-gray-900 to-black pointer-events-none -z-10"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Brand & About */}
          <div className="space-y-6">
            <Link to="/" className="text-3xl font-bold flex items-center gap-2 group">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600 drop-shadow-lg">
                HuluOrder
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-gray-400 font-medium">
              Your premier destination for buying and selling quality products.
              We connect verified sellers with eager buyers in a secure,
              seamless, and innovative marketplace.
            </p>
            {/* Social Media Section */}
            <div className="flex gap-4 pt-2">
              {[
                { icon: Telegram, href: "https://t.me/huluorder", color: "hover:bg-blue-700" },
                {
                  icon: () => (
                    <svg fill="currentColor" viewBox="0 0 448 512" className="w-5 h-5">
                      <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25V349.38A162.55 162.55 0 1 1 185 188.31V278.2a74.62 74.62 0 1 0 52.23 71.18V0l88 0a121.18 121.18 0 0 0 1.86 22.17h0A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z" />
                    </svg>
                  ),
                  href: "https://www.tiktok.com/@huluorder",
                  color: "hover:bg-black"
                },
                { icon: Facebook, href: "https://web.facebook.com/people/Hulu-Order/61560011031418/#", color: "hover:bg-blue-600" },
                { icon: Instagram, href: "https://www.instagram.com/huluorder/", color: "hover:bg-pink-600" },
              ].map((Social, i) => (
                <a
                  key={i}
                  href={Social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center transition-all duration-300 text-white shadow-lg transform hover:-translate-y-2 hover:scale-110 ${Social.color}`}
                >
                  <Social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Company Section */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-gray-100 to-gray-400">Company</h3>
            <ul className="space-y-4 flex flex-col">
              {[
                { name: "About Us", path: "/about" },
                { name: "Terms & Conditions", path: "/terms" },
                { name: "Privacy Policy", path: "/privacy" },
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="inline-flex items-center group text-sm font-medium text-gray-400 hover:text-green-400 transition-colors duration-300"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-green-500" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Section */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-gray-100 to-gray-400">Support</h3>
            <ul className="space-y-4 flex flex-col">
              {[
                { name: "Help Center", path: "/help" },
                { name: "How it works", path: "/how-it-works" },
                { name: "Trust & Safety", path: "/safety" },
                { name: "Selling Tips", path: "/tips" },
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="inline-flex items-center group text-sm font-medium text-gray-400 hover:text-green-400 transition-colors duration-300"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-green-500" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="text-white text-lg font-bold mb-6 uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-gray-100 to-gray-400">Contact Us</h3>
            <ul className="space-y-5 text-sm font-medium text-gray-400">
              <li className="flex items-start gap-4 hover:text-white transition-colors duration-300 group">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Ethiopia+Addis+Ababa+Megenagna+Betelhem+Plaza"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4"
                >
                  <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-green-600/20 transition-colors duration-300 mt-1 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-green-500 group-hover:text-green-400" />
                  </div>
                  <span className="leading-relaxed">Ethiopia, Addis Ababa<br /> Megenagna, Betelhem Plaza</span>
                </a>
              </li>
              <li className="flex items-center gap-4 hover:text-white transition-colors duration-300 group">
                <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-green-600/20 transition-colors duration-300 flex-shrink-0">
                  <Phone className="w-5 h-5 text-green-500 group-hover:text-green-400" />
                </div>
                <a href="tel:+251906103333" className="hover:text-green-400 transition-colors">+251 90 6103 333</a>
              </li>
              <li className="flex items-center gap-4 hover:text-white transition-colors duration-300 group">
                <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-green-600/20 transition-colors duration-300 flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-500 group-hover:text-green-400" />
                </div>
                <a href="mailto:support@huluorder.com" className="hover:text-green-400 transition-colors">support@huluorder.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800/80 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500 font-medium">
            © {new Date().getFullYear()} HuluOrder. All rights reserved.
          </p>
          <div className="flex gap-8 text-sm font-medium text-gray-500">
            <Link to="/privacy" className="hover:text-green-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-green-400 transition-colors">Terms of Service</Link>
            <Link to="/sitemap" className="hover:text-green-400 transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
